import { Platform } from "react-native";

const GTM_CONTAINER_ID = 'GTM-KHFSLNT';
const PURCHASE_STORAGE_KEY = 'gtm_pending_purchase';
const PURCHASE_SENT_KEY = 'gtm_last_purchase_signature';

const isWeb = () => Platform.OS === 'web' && typeof window !== 'undefined';

const ensureDataLayer = () => {
  if (!isWeb()) return null;
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

export const loadGoogleTagManager = () => {
  if (!isWeb()) return false;

  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    //console.log('🔧 Desarrollo local detectado - GTM no cargado');
    return false;
  }

  if (window.__gtmLoaded) return true;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  const existingScript = document.querySelector(`script[src*="googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}"]`);
  if (!existingScript) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
    document.head.appendChild(script);
  }

  const existingNoscript = document.getElementById('gtm-noscript-container');
  if (!existingNoscript && document.body) {
    const noscript = document.createElement('noscript');
    noscript.id = 'gtm-noscript-container';
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_CONTAINER_ID}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);
  }

  window.__gtmLoaded = true;
  //console.log('✅ Google Tag Manager cargado en producción');
  return true;
};

export const pushDataLayerEvent = (eventName, payload = {}) => {
  const dataLayer = ensureDataLayer();
  if (!dataLayer) return;

  const eventPayload = {
    event: eventName,
    ...payload,
  };

  dataLayer.push(eventPayload);
 // console.log(`📊 dataLayer.push: ${eventName}`, eventPayload);
};

export const buildPagePathFromRoute = (route) => {
  if (!route) return '/home';

  const { name, params } = route;

  switch (name) {
    case 'Home':
      return '/home';
    case 'Productos':
      return '/productos';
    case 'DescripcionProducto':
      return `/producto/${params?.id ?? ''}`.replace(/\/$/, '');
    case 'PromosBancos':
      return '/PromosBanco';
    case 'Historia':
      return '/Historia';
    case 'Manifiesto':
      return '/manifiesto';
    case 'PoliticasDevolucion':
      return '/politicas-de-devolucion';
    case 'TerminosYCondiciones':
      return '/terminos-y-condiciones';
    case 'ArrepentimientoDeCompra':
      return '/arrepentimiento-de-compra';
    case 'validar':
      return '/validar';
    case 'CheckoutFinalizarCompra':
      return '/checkout';
    case 'PromocionDetalle':
      return `/promocion/${params?.id ?? ''}`.replace(/\/$/, '');
    case 'Perfil':
      return '/perfil';
    case 'PagoOK':
    case 'PagoOKWeb':
      return '/pagoOK';
    case 'PagoFail':
    case 'PagoFailWeb':
      return '/pagoFail';
    default:
      return '/404';
  }
};

export const trackPageView = (route) => {
  if (!isWeb()) return;

  const page_path = buildPagePathFromRoute(route);
  const page_location = `${window.location.origin}${page_path}`;
  const page_title = route?.name || document.title || 'Sin título';

  pushDataLayerEvent('page_view', {
    page_path,
    page_location,
    page_title,
    screen_name: route?.name || '',
  });
};

// ==========================================
// REFACTORIZADO: GA4 ECOMMERCE NATIVO
// ==========================================

export const trackAddToCart = ({ plu, quantity, product_name, price, item_category } = {}) => {
  const itemPrice = Number(price || 0);
  const itemQty = Number(quantity || 1);

  pushDataLayerEvent('add_to_cart', {
    ecommerce: {
      currency: 'ARS',
      value: itemPrice * itemQty,
      items: [
        {
          item_id: plu ? String(plu) : '',
          item_name: product_name || '',
          price: itemPrice,
          quantity: itemQty,
          item_category: item_category || 'Supermercado'
        }
      ]
    }
  });
};

export const savePendingPurchase = (payload = {}) => {
  if (!isWeb()) return;

  // Mapea internamente el array "productos" recibido al formato exigido por Google
  const ga4Items = Array.isArray(payload.productos)
    ? payload.productos.map((prod) => ({
        item_id: prod.plu ? String(prod.plu) : '',
        item_name: prod.descripcion || prod.nombre || '',
        price: Number(prod.precio || prod.preUni || 0),
        quantity: Number(prod.cantCarrito || prod.cant || 1),
        item_category: prod.rubro || 'Supermercado'
      }))
    : [];

  const normalized = {
    transaction_id: payload.transaction_id || `${Date.now()}`,
    value: Number(payload.value || 0),
    currency: payload.currency || 'ARS',
    tax: Number(payload.tax || 0),
    shipping: Number(payload.shipping || 0),
    payment_method: payload.payment_method || '',
    items: ga4Items
  };

  window.localStorage.setItem(PURCHASE_STORAGE_KEY, JSON.stringify(normalized));
};

export const clearPendingPurchase = () => {
  if (!isWeb()) return;
  window.removeItem(PURCHASE_STORAGE_KEY);
};

export const getPendingPurchase = () => {
  if (!isWeb()) return null;
  const raw = window.localStorage.getItem(PURCHASE_STORAGE_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
};

export const trackPurchase = (purchasePayload = null) => {
  if (!isWeb()) return false;

  const payload = purchasePayload || getPendingPurchase();
  if (!payload) return false;

  // Validación anti-duplicados basada en ID de transacción y valor total
  const signature = JSON.stringify({
    transaction_id: payload.transaction_id || '',
    value: payload.value || 0,
  });

  const lastSignature = window.sessionStorage.getItem(PURCHASE_SENT_KEY);
  if (lastSignature === signature) {
    return false;
  }

  pushDataLayerEvent('purchase', {
    ecommerce: {
      transaction_id: payload.transaction_id,
      value: payload.value,
      currency: payload.currency || 'ARS',
      tax: payload.tax || 0,
      shipping: payload.shipping || 0,
      items: payload.items
    },
    payment_method: payload.payment_method || ''
  });

  window.sessionStorage.setItem(PURCHASE_SENT_KEY, signature);
  clearPendingPurchase();
  return true;
};

export const trackCreateAccountClick = ({ email } = {}) => {
  pushDataLayerEvent('create_account_click', {
    email: email || '',
  });
};

export const trackCompleteRegistration = ({ email } = {}) => {
  pushDataLayerEvent('complete_registration', {
    status: 'verified',
    email: email || '',
  });
};